import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, AlertCircle, Shield, Building2 } from 'lucide-react';
import { companyAuthService } from '../../services/companyAuthService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const CompanyLogin = () => {
  const navigate = useNavigate();
  const { companyIdentifier } = useParams();
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [company, setCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      if (companyIdentifier) {
        navigate(`/company/${companyIdentifier}/dashboard`);
      } else {
        navigate('/client-dashboard-home');
      }
    }
  }, [user, navigate, companyIdentifier]);

  // Load company details if company identifier provided
  useEffect(() => {
    const loadCompany = async () => {
      if (!companyIdentifier) {
        setLoadingCompany(false);
        return;
      }

      setLoadingCompany(true);
      const { data, error } = await companyAuthService?.getCompanyBySlug(companyIdentifier);
      
      if (error) {
        setError(error?.message);
        setLoadingCompany(false);
        return;
      }

      setCompany(data);
      setLoadingCompany(false);
    };

    loadCompany();
  }, [companyIdentifier]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error } = await companyAuthService?.signIn(
        email, 
        password, 
        companyIdentifier
      );

      if (error) {
        setError(error?.message);
        setLoading(false);
        return;
      }

      // Fetch user profile to determine role and company for redirect
      const { data: profileData, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('role, company_id, companies(slug)')
        ?.eq('id', data?.user?.id)
        ?.single();

      if (profileError) {
        setError('Failed to load user profile. Please try again.');
        setLoading(false);
        return;
      }

      // Role-based redirect - ensure correct dashboard for user type
      if (profileData?.role === 'admin') {
        navigate('/admin-dashboard');
      } else if (profileData?.role === 'company_admin' && profileData?.companies?.slug) {
        navigate(`/company/${profileData?.companies?.slug}/dashboard`);
      } else {
        // Default fallback for other user types
        navigate('/client-dashboard-home');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  if (loadingCompany) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Company Branding Header */}
        {company && (
          <div className="text-center space-y-4">
            {company?.logo && (
              <div className="flex justify-center">
                <img 
                  src={company?.logo} 
                  alt={`${company?.name} logo`}
                  className="h-16 w-auto"
                />
              </div>
            )}
            <div className="flex items-center justify-center gap-2 text-slate-600">
              <Building2 className="h-5 w-5" style={{ color: company?.primaryColor }} />
              <h2 className="text-2xl font-bold" style={{ color: company?.primaryColor }}>
                {company?.name}
              </h2>
            </div>
            <p className="text-sm text-slate-500">Company Dashboard Login</p>
          </div>
        )}

        {!company && (
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-3 rounded-2xl shadow-lg">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-slate-800">Company Login</h2>
            <p className="mt-2 text-sm text-slate-600">
              Sign in to access your company dashboard
            </p>
          </div>
        )}

        {/* Login Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-lg py-2 px-4">
            <Shield className="h-4 w-4" />
            <span>Secure Company Access</span>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Sign In Failed</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                Company Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-slate-900 placeholder-slate-400"
                  placeholder="admin@company.com"
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  className="block w-full pl-10 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-slate-900 placeholder-slate-400"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-700">
                  Remember me
                </label>
              </div>
              <button
                type="button"
                className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors"
              >
                Forgot password?
              </button>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              style={company ? {
                background: `linear-gradient(to right, ${company?.primaryColor}, ${company?.secondaryColor})`
              } : undefined}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <Lock className="h-5 w-5" />
                  <span>Sign In to Dashboard</span>
                </>
              )}
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Demo Company Credentials
              </p>
              <div className="text-xs text-blue-700 space-y-1 font-mono">
                <p>Premium Epoxy: admin@premiumepoxycoatings.com / premium123</p>
                <p>FloorTech: admin@floortechepoxy.com / floortech123</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="text-center text-sm text-slate-600">
          <p>
            Platform administrator?{' '}
            <button
              onClick={() => navigate('/admin-login')}
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
            >
              Admin Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompanyLogin;